import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { backtestApi, strategyApi } from '../../lib/apiClient.js';

// Track-record ledger UI — renders the backend's append-only ledger
// (GET /api/backtest/ledger/mine). This is the Schedule-B evidence base:
// every compute-metaged run lands here with its metrics + a copyable
// strategySpec. From here a seat can publish a run as a marketplace strategy.
export default function TrackRecord() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null); // null = not loaded yet
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(null); // runId currently publishing
  const [publishError, setPublishError] = useState('');

  async function load() {
    setError('');
    try {
      const data = await backtestApi.ledger();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load your track record.');
      setRows([]);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function publish(runId) {
    setPublishing(runId);
    setPublishError('');
    try {
      await strategyApi.publish({ ledgerRunId: runId });
      navigate('/app/strategies?published=1');
    } catch (err) {
      setPublishError(err.message || 'Publish failed.');
      setPublishing(null);
    }
  }

  const withMetrics = (rows || []).filter((r) => r.metrics?.overall);
  const returns = withMetrics
    .map((r) => Number(r.metrics.overall.totalReturn))
    .filter((v) => Number.isFinite(v));
  const bestReturn = returns.length ? Math.max(...returns) : null;

  return (
    <Card className="aq-history">
      <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
        Track record
      </h3>
      <p className="aq-module-desc">
        Your append-only backtest ledger — the evidence base for the verified track record and strategy copier
        (Schedule B). Every compute-metaged run is recorded here with its metrics.
      </p>

      {withMetrics.length > 0 && (
        <div className="aq-stat-row" style={{ marginBottom: 12 }}>
          <span className="aq-stat">
            Runs: <strong className="num">{(rows || []).length}</strong>
          </span>
          <span className="aq-stat">
            Best return: <strong className="num">{bestReturn != null ? `${bestReturn.toFixed(2)}%` : '—'}</strong>
          </span>
          <span className="aq-stat">
            Total compute charged: <strong className="num">${(rows || []).reduce((s, r) => s + (r.charged || 0), 0).toFixed(2)}</strong>
          </span>
        </div>
      )}

      {error && <p className="aq-auth-alert aq-auth-alert--error">{error}</p>}
      {rows === null && <p className="aq-module-desc">Loading ledger…</p>}
      {rows && rows.length === 0 && !error && (
        <p className="aq-module-desc">
          No runs yet. Run a backtest in Studio and it will be recorded here, ready to publish as a strategy.
        </p>
      )}
      {rows && rows.length > 0 && (
        <div className="aq-history-list">
          {rows.map((r) => {
            const m = r.metrics?.overall;
            return (
              <div key={r.runId} className="aq-history-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'default' }}>
                <span className="aq-history-mode">{r.mode || r.runId?.slice(0, 8)}</span>
                <span className="aq-history-date">{new Date(r.submittedAt).toLocaleString()}</span>
                <span className="aq-history-meta">
                  {m
                    ? `${(m.totalReturn ?? 0).toFixed(2)}% · Sharpe ${(m.sharpe ?? 0).toFixed(2)} · DD ${(m.maxDrawdown ?? 0).toFixed(2)}%`
                    : '—'}
                  {r.symbols?.length ? ` · ${r.symbols.slice(0, 3).join(', ')}${r.symbols.length > 3 ? '…' : ''}` : ''}
                </span>
                <span style={{ flex: 1 }} />
                <span className="aq-news-meta">{r.compute}</span>
                <span className="aq-news-meta num">${(r.charged || 0).toFixed(2)}</span>
                {r.strategySpec && (
                  <Button size="sm" variant="secondary" disabled={publishing === r.runId} onClick={() => publish(r.runId)}>
                    {publishing === r.runId ? 'Publishing…' : 'Publish strategy'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {publishError && <p className="aq-auth-alert aq-auth-alert--error">{publishError}</p>}
      <p className="aq-module-desc" style={{ marginTop: 12 }}>
        Ready to share? Publish a strategy to the <a href="/app/strategies">Strategy Marketplace</a>.
      </p>
    </Card>
  );
}